import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Clock, Building2, Globe2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Seo, organizationSchema, SITE_URL } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Elikya Technology",
  url: `${SITE_URL}/contact`,
  description: "Talk to Elikya Technology about scaling your payment operations.",
};

const Input = ({ id, label, type = "text", value, onChange, placeholder, autoComplete, error }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-ink">
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={!!error}
      data-testid={`contact-${id}`}
      className={`brand-ring mt-1.5 w-full rounded-xl border bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 transition-shadow ${
        error ? "border-rose-400" : "border-stroke"
      }`}
    />
    {error && (
      <p data-testid={`error-${id}`} className="mt-1 text-xs text-rose-600">
        {error}
      </p>
    )}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    scope: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.email.trim()) e.email = "Work email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!form.company.trim()) e.company = "Business name is required";
    if (!form.scope.trim()) e.scope = "Please tell us about your needs";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setErrors({});
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setLoading(false);
    toast.success("Thank you! Our payments specialist will contact you within 1 business day.");
    setForm({ firstName: "", lastName: "", email: "", company: "", scope: "" });
  };

  return (
    <Layout>
      <Seo
        title="Let's Scale Your Payment Operations"
        description="Talk to Elikya Technology — incorporated in Newark, Delaware, US, with regional hubs in South Africa and DR Congo. Dedicated business response within 1 business day."
        keywords="contact elikya technology, elikya tech support, payments africa, merchant payment support, enterprise payments africa, fintech africa, cross-border payments africa"
        path="/contact"
        ogImage="/og-contact.png"
        jsonLd={[organizationSchema, contactSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stroke">
        <div className="pointer-events-none absolute inset-0 grid-dots opacity-60" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
          <Reveal className="max-w-3xl">
            <span className="text-sm font-semibold text-brand">Contact</span>
            <h1 className="mt-3 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
              Let's Scale Your Payment Operations
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slatey sm:text-lg">
              Whether you're accepting your first payment or processing millions a month, our payments
              specialists will help you grow with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          {/* LEFT — company info */}
          <div className="space-y-5">
            <Reveal>
              <article className="rounded-2xl border border-stroke bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Building2 className="h-4 w-4 text-brand" /> Incorporation
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slatey">
                  Elikya Technology, Inc.
                  <br />
                  <span className="font-medium text-ink">Newark, Delaware, United States</span>
                </p>
              </article>
            </Reveal>

            <Reveal delay={0.06}>
              <article className="rounded-2xl border border-stroke bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Globe2 className="h-4 w-4 text-brand" /> Regional Operations
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {["South Africa", "DR Congo"].map((r) => (
                    <div key={r} className="rounded-xl border border-stroke bg-canvas px-4 py-3">
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                        <MapPin className="h-3.5 w-3.5 text-brand" /> {r}
                      </p>
                    </div>
                  ))}
                </div>
              </article>
            </Reveal>

            <Reveal delay={0.12}>
              <article className="rounded-2xl border border-stroke bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Mail className="h-4 w-4 text-brand" /> Direct Channels
                </div>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <a href="mailto:hello@elikya.tech" className="font-medium text-brand hover:text-brand-hover">
                      hello@elikya.tech
                    </a>
                    <span className="ml-2 text-slatey">· General</span>
                  </li>
                  <li>
                    <a href="mailto:dev@elikya.tech" className="font-medium text-brand hover:text-brand-hover">
                      dev@elikya.tech
                    </a>
                    <span className="ml-2 text-slatey">· Technical</span>
                  </li>
                </ul>
              </article>
            </Reveal>

            <Reveal delay={0.18}>
              <article className="rounded-2xl border border-brand/20 bg-brand-tint p-6">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Clock className="h-4 w-4 text-brand" /> SLA Guarantee
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  Dedicated business response within{" "}
                  <span className="font-semibold">1 business day</span>.
                </p>
              </article>
            </Reveal>
          </div>

          {/* RIGHT — form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              noValidate
              data-testid="contact-form"
              className="rounded-2xl border border-stroke bg-white p-7 shadow-float"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Input id="firstName" label="First Name" value={form.firstName} onChange={set("firstName")} placeholder="Amara" autoComplete="given-name" error={errors.firstName} />
                <Input id="lastName" label="Last Name" value={form.lastName} onChange={set("lastName")} placeholder="Okonkwo" autoComplete="family-name" error={errors.lastName} />
              </div>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <Input id="email" label="Work Email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" autoComplete="email" error={errors.email} />
                <Input id="company" label="Business Name" value={form.company} onChange={set("company")} placeholder="Acme Trading" autoComplete="organization" error={errors.company} />
              </div>

              <div className="mt-6">
                <label htmlFor="scope" className="block text-sm font-medium text-ink">
                  Business Needs
                </label>
                <textarea
                  id="scope"
                  name="scope"
                  rows={4}
                  value={form.scope}
                  onChange={set("scope")}
                  placeholder="Tell us about your business, the markets you serve and how you'd like to get paid…"
                  data-testid="contact-scope"
                  aria-invalid={!!errors.scope}
                  className={`brand-ring mt-1.5 w-full resize-none rounded-xl border bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 transition-shadow ${
                    errors.scope ? "border-rose-400" : "border-stroke"
                  }`}
                />
                {errors.scope && (
                  <p data-testid="error-scope" className="mt-1 text-xs text-rose-600">
                    {errors.scope}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-hover disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
